import { CrosswordWord, CrosswordPair, getCompactCrosswordSize } from './CrosswordText'
import { BadgeStack, StatusBadge } from './StatusFilter'
import type { PairingStatus, ClueStatus } from '../types'

type WordListItemProps = {
  text: string
  inverted?: boolean
  autoHighlight?: boolean
  selected?: boolean
  isPlaced?: boolean
  variantCount?: number | undefined
  listLabel?: string
  letterCount?: number
  onClick?: () => void
  className?: string
}

/**
 * Shared component for displaying a word in a list
 */
export function WordListItem({
  text,
  inverted = false,
  autoHighlight = false,
  selected = false,
  isPlaced = false,
  variantCount,
  listLabel,
  letterCount,
  onClick,
  className = ''
}: WordListItemProps) {
  const Component = onClick ? 'button' : 'div'

  return (
    <Component
      onClick={onClick}
      className={`w-full flex items-center text-left px-1.5 py-1 rounded-lg text-sm transition-colors ${selected ? 'bg-gray-100 text-gray-900' : 'hover:bg-gray-50'
        } ${className}`}
    >
      <CrosswordWord text={text} autoHighlight={autoHighlight} inverted={inverted} isPlaced={isPlaced} />
      {variantCount && variantCount > 0 && (
        <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-medium bg-violet-100 text-violet-600 rounded" title="Has variants">
          +{variantCount}
        </span>
      )}
      {listLabel && (
        <span className="text-xs text-gray-400 ml-auto">{listLabel}</span>
      )}
      {letterCount !== undefined && !listLabel && (
        <span className="text-gray-400 ml-auto text-xs tabular-nums">{letterCount}</span>
      )}
    </Component>
  )
}

type PairingListItemProps = {
  textA: string
  textB: string
  autoHighlight?: boolean
  selected?: boolean
  isPlaced?: boolean
  isPlacedA?: boolean
  isPlacedB?: boolean
  pairingStatus?: PairingStatus | undefined
  clueStatus?: ClueStatus | 'empty' | undefined
  clueText?: string | undefined
  showNoClueText?: boolean | undefined
  onClick?: (() => void) | undefined
  onPairingStatusChange?: ((status: PairingStatus) => void) | undefined
  className?: string | undefined
}

/**
 * Shared component for displaying a pairing in a list
 */
export function PairingListItem({
  textA,
  textB,
  autoHighlight = false,
  selected = false,
  isPlaced = false,
  isPlacedA,
  isPlacedB,
  pairingStatus = 'none',
  clueStatus = 'empty',
  clueText,
  showNoClueText = false,
  onClick,
  onPairingStatusChange,
  className = ''
}: PairingListItemProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.() }}
      className={`w-full text-left p-2 rounded-lg transition-colors cursor-pointer ${selected ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
        } ${className}`}
    >
      {/* Clue text on top if provided */}
      {clueText ? (
        <p className="text-sm text-gray-700 mb-2 line-clamp-2">
          {clueText}
        </p>
      ) : showNoClueText ? (
        <p className="text-sm text-gray-300 italic mb-2">No clue</p>
      ) : null}

      {/* Main row: badges on left, pair on right */}
      <div className="flex items-center gap-2">
        {/* Status badges - always show both, vertically stacked like crossword cells */}
        <BadgeStack pairingStatus={pairingStatus} clueStatus={clueStatus} onPairingStatusChange={onPairingStatusChange} />

        {/* Pair display */}
        <CrosswordPair textA={textA} textB={textB} autoHighlight={autoHighlight} isPlaced={isPlaced} {...(isPlacedA !== undefined && { isPlacedA })} {...(isPlacedB !== undefined && { isPlacedB })} />
      </div>
    </div>
  )
}

type PairingGridItemProps = {
  textA: string
  textB: string
  autoHighlight?: boolean
  selected?: boolean
  isPlaced?: boolean
  pairingStatus?: PairingStatus | undefined
  onClick?: () => void
  className?: string
}

/**
 * Compact pairing item for grid layouts (e.g., possible pairings)
 */
export function PairingGridItem({
  textA,
  textB,
  autoHighlight = false,
  selected = false,
  isPlaced = false,
  pairingStatus,
  onClick,
  className = ''
}: PairingGridItemProps) {
  const showBadge = pairingStatus && pairingStatus !== 'none'
  const compactSize = getCompactCrosswordSize([textA, textB])
  const maxLetters = Math.max(textA.replace(/\s/g, '').length, textB.replace(/\s/g, '').length)
  const needsFullWidth = maxLetters > 7

  return (
    <button
      onClick={onClick}
      className={`relative min-w-0 overflow-hidden p-2 rounded-lg transition-colors flex items-center ${needsFullWidth ? 'col-span-2 justify-start' : 'justify-center'} ${selected ? 'bg-gray-100 ring-1 ring-gray-300' : 'hover:bg-gray-50'
        } ${className}`}
    >
      {showBadge && (
        <div className="absolute top-1 right-1">
          <StatusBadge status={pairingStatus} compact />
        </div>
      )}
      <CrosswordPair
        textA={textA}
        textB={textB}
        size={compactSize}
        autoHighlight={autoHighlight}
        isPlaced={isPlaced}
      />
    </button>
  )
}
